import { useState, useEffect, useRef, Fragment } from 'react'
import api from '../services/api'

const TIPOS = [
  { value: 'VEN', label: 'VEN — Venda' },
  { value: 'DEV', label: 'DEV — Devolução' },
  { value: 'RCS', label: 'RCS — Remessa/Retorno' },
  { value: 'OUT', label: 'OUT — Outros' },
  { value: 'TER', label: 'TER — Terceiros' },
  { value: 'CTO', label: 'CTO — CT-e OS' },
  { value: 'MUN', label: 'MUN — Nota Municipal' },
]

const SITUACOES = [
  { value: '6', label: 'Autorizada' },
  { value: '7', label: 'Cancelada' },
]

interface Item { its_cod: number; pnv_des: string; pnv_qt_ven: string; pnv_preco: string; pnv_vl_mer: string }
interface Nota {
  ntv_empcod: number; ntv_serie: string; ntv_numero: number; cli_cod: string; cliente: string
  ntv_datemi: string; ntv_tipope: string; ntv_sitnfe: number; cfop: string
  valor_bruto: string; valor_liquido: string; itens?: Item[]
}
interface Empresa { empcod: number; empnom: string; empfan: string; empcgc: string }

const SITUACAO_LABEL: Record<number, string> = { 6: 'Autorizada', 7: 'Cancelada' }

function hoje() { return new Date().toISOString().slice(0, 10) }
function primeiroDiaMes() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01` }

function fmtMoeda(v: string | number) {
  return Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
function fmtData(v: string) {
  return v ? String(v).slice(0, 10).split('-').reverse().join('/') : ''
}
// ── Combo com checkboxes (multi-seleção) ──────────────────────────────────────
function ComboCheck({ label, opcoes, selecionados, onChange, placeholder, width }: {
  label: string
  opcoes: { value: string; label: string }[]
  selecionados: string[]
  onChange: (v: string[]) => void
  placeholder: string
  width?: number
}) {
  const [aberto, setAberto] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setAberto(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  function toggle(v: string) {
    onChange(selecionados.includes(v) ? selecionados.filter(x => x !== v) : [...selecionados, v])
  }

  const resumo = selecionados.length === 0
    ? placeholder
    : selecionados.length === opcoes.length
      ? 'Todas'
      : selecionados.length === 1
        ? opcoes.find(o => o.value === selecionados[0])?.label ?? '1 selecionada'
        : `${selecionados.length} selecionadas`

  return (
    <div className="field" style={{ position: 'relative', width: width ?? 200, flex: '0 0 auto' }} ref={ref}>
      <label>{label}</label>
      <button
        type="button"
        onClick={() => setAberto(o => !o)}
        style={{
          width: '100%', textAlign: 'left', background: '#fff', border: '1px solid #d1d5db',
          borderRadius: 6, padding: '0.45rem 0.6rem', fontSize: '0.85rem', cursor: 'pointer',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          color: selecionados.length ? '#111827' : '#9ca3af',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{resumo}</span>
        <span style={{ color: '#9ca3af', marginLeft: 6 }}>{aberto ? '▴' : '▾'}</span>
      </button>

      {aberto && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, marginTop: 2,
          background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
          maxHeight: 260, overflowY: 'auto', boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', borderBottom: '1px solid #eee', background: '#fafafa' }}>
            <button type="button" onClick={() => onChange(opcoes.map(o => o.value))}
              style={{ fontSize: '0.72rem', color: '#1a6ea8', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
              Marcar todas
            </button>
            <button type="button" onClick={() => onChange([])}
              style={{ fontSize: '0.72rem', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}>
              Limpar
            </button>
          </div>
          {opcoes.map(o => (
            <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', fontSize: '0.82rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={selecionados.includes(o.value)} onChange={() => toggle(o.value)}
                style={{ width: 15, height: 15, flexShrink: 0, margin: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

export default function RelatorioNfeSaida() {
  const [dataIni, setDataIni] = useState(primeiroDiaMes())
  const [dataFim, setDataFim] = useState(hoje())
  const [empresas, setEmpresas] = useState<Empresa[]>([])
  const [empresasSel, setEmpresasSel] = useState<string[]>([])
  const [tiposSel, setTiposSel] = useState<string[]>([])
  const [situacoesSel, setSituacoesSel] = useState<string[]>([])
  const [listarItens, setListarItens] = useState(false)

  const [buscaCliente, setBuscaCliente] = useState('')
  const [clienteSel, setClienteSel] = useState<{ cli_cod: string; nome: string } | null>(null)
  const [sugestoesCliente, setSugestoesCliente] = useState<{ cli_cod: string; cli_nom: string; cli_fan: string }[]>([])

  const [veiCod, setVeiCod] = useState('')
  const [veiPlaca, setVeiPlaca] = useState('')
  const [veiAviso, setVeiAviso] = useState('')

  const [carregando, setCarregando] = useState(false)
  const [gerandoPdf, setGerandoPdf] = useState(false)
  const [gerandoExcel, setGerandoExcel] = useState(false)
  const [erro, setErro] = useState('')
  const [notas, setNotas] = useState<Nota[] | null>(null)

  useEffect(() => {
    api.get('/erp/empresas').then(r => setEmpresas(r.data)).catch(() => {})
  }, [])

  async function buscarClientes(termo: string) {
    setBuscaCliente(termo)
    setClienteSel(null)
    if (termo.trim().length < 2) { setSugestoesCliente([]); return }
    const r = await api.get('/erp/clientes/lista', { params: { busca: termo, por_pagina: 10 } })
    setSugestoesCliente(r.data)
  }
  function selecionarCliente(c: { cli_cod: string; cli_nom: string; cli_fan: string }) {
    setClienteSel({ cli_cod: c.cli_cod, nome: c.cli_fan || c.cli_nom })
    setBuscaCliente(c.cli_fan || c.cli_nom)
    setSugestoesCliente([])
  }

  async function buscarVeiculo() {
    setVeiAviso('')
    setVeiPlaca('')
    if (!veiCod.trim()) return
    try {
      const r = await api.get(`/relatorios/veiculo/${veiCod.trim()}`)
      if (r.data) setVeiPlaca(r.data.veipla)
      else setVeiAviso('Veículo não encontrado.')
    } catch {
      setVeiAviso('Veículo não encontrado.')
    }
  }

  function filtrosParams() {
    return {
      dataIni, dataFim,
      empresas: empresasSel.join(','),
      cliCod: clienteSel?.cli_cod,
      veiCod: veiCod || undefined,
      tipos: tiposSel.join(','),
      situacoes: situacoesSel.join(','),
      listarItens: listarItens ? 'S' : 'N',
    }
  }

  async function gerar() {
    setCarregando(true)
    setErro('')
    setNotas(null)
    try {
      const r = await api.get('/relatorios/nfe-saida', { params: filtrosParams() })
      setNotas(r.data)
    } catch (e: any) {
      setErro(e.response?.data?.error ?? 'Erro ao gerar relatório.')
    } finally {
      setCarregando(false)
    }
  }

  async function gerarPdf() {
    setGerandoPdf(true)
    setErro('')
    try {
      const r = await api.get('/relatorios/nfe-saida/pdf', { params: filtrosParams(), responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
      window.open(url, '_blank')
    } catch (e: any) {
      setErro('Erro ao gerar PDF.')
    } finally {
      setGerandoPdf(false)
    }
  }

  async function gerarExcel() {
    setGerandoExcel(true)
    setErro('')
    try {
      const r = await api.get('/relatorios/nfe-saida/excel', { params: filtrosParams(), responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }))
      const a = document.createElement('a')
      a.href = url
      a.download = 'relatorio-nfe-saida.xlsx'
      a.click()
      URL.revokeObjectURL(url)
    } catch (e: any) {
      setErro('Erro ao gerar Excel.')
    } finally {
      setGerandoExcel(false)
    }
  }

  const totalBruto = notas?.reduce((s, n) => s + Number(n.valor_bruto || 0), 0) ?? 0
  const totalLiquido = notas?.reduce((s, n) => s + Number(n.valor_liquido || 0), 0) ?? 0

  return (
    <div>
      <div className="card" style={{ marginBottom: '1rem' }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1.1rem', color: '#1a6ea8' }}>
          Relatório de Notas Fiscais de Saída
        </h2>

        {/* ── Período e origem ─────────────────────────────────────────── */}
        <div style={{ background: '#f7faff', borderLeft: '4px solid #1a6ea8', borderRadius: 8, padding: '0.9rem 1.1rem', marginBottom: '0.9rem' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.9rem', alignItems: 'flex-start' }}>
            <div className="field" style={{ width: 130, flex: '0 0 auto' }}>
              <label>Emissão de</label>
              <input type="date" value={dataIni} onChange={e => setDataIni(e.target.value)} />
            </div>
            <div className="field" style={{ width: 130, flex: '0 0 auto' }}>
              <label>Emissão até</label>
              <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} />
            </div>
            <ComboCheck
              label="Empresas"
              placeholder="Todas"
              width={200}
              opcoes={empresas.map(e => ({ value: String(e.empcod), label: `${e.empcod} — ${e.empfan || e.empnom}` }))}
              selecionados={empresasSel}
              onChange={setEmpresasSel}
            />
            <ComboCheck
              label="Tipo de NF"
              placeholder="Todos"
              width={170}
              opcoes={TIPOS}
              selecionados={tiposSel}
              onChange={setTiposSel}
            />
            <ComboCheck
              label="Situação da NF"
              placeholder="Autorizada + Cancelada"
              width={190}
              opcoes={SITUACOES}
              selecionados={situacoesSel}
              onChange={setSituacoesSel}
            />
          </div>
        </div>

        {/* ── Cliente e veículo ────────────────────────────────────────── */}
        <div style={{ background: '#fff8f2', borderLeft: '4px solid #f07820', borderRadius: 8, padding: '0.9rem 1.1rem', marginBottom: '0.9rem' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.9rem', alignItems: 'flex-start' }}>
            <div className="field" style={{ position: 'relative', width: 260, flex: '0 0 auto' }}>
              <label>Cliente</label>
              <input
                type="text"
                placeholder="Buscar por nome... (vazio = todos)"
                value={buscaCliente}
                onChange={e => buscarClientes(e.target.value)}
              />
              {sugestoesCliente.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                  background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
                  maxHeight: 220, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }}>
                  {sugestoesCliente.map(c => (
                    <button key={c.cli_cod} type="button" onClick={() => selecionarCliente(c)}
                      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.82rem' }}>
                      {c.cli_fan || c.cli_nom}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="field" style={{ width: 160, flex: '0 0 auto' }}>
              <label>Código do Veículo</label>
              <input type="text" placeholder="Vazio = todos" value={veiCod}
                onChange={e => { setVeiCod(e.target.value); setVeiPlaca(''); setVeiAviso('') }}
                onBlur={buscarVeiculo} />
              {veiPlaca && <div style={{ fontSize: '0.72rem', color: '#15803d', marginTop: 2 }}>Placa: {veiPlaca}</div>}
              {veiAviso && <div style={{ fontSize: '0.72rem', color: '#dc2626', marginTop: 2 }}>{veiAviso}</div>}
            </div>
          </div>
        </div>

        {/* ── Opções ───────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1.2rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', cursor: 'pointer', fontWeight: 500 }}>
            <input type="checkbox" checked={listarItens} onChange={e => setListarItens(e.target.checked)} />
            Listar itens da NF
          </label>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="btn btn-primary" onClick={gerar} disabled={carregando}>
            {carregando ? 'Gerando...' : 'Gerar relatório'}
          </button>
          {notas && (
            <button
              type="button"
              onClick={gerarPdf}
              disabled={gerandoPdf}
              style={{
                padding: '0.5rem 1rem', background: '#dc2626', color: '#fff', border: 'none',
                borderRadius: 6, fontWeight: 600, fontSize: '0.85rem', cursor: gerandoPdf ? 'not-allowed' : 'pointer',
                opacity: gerandoPdf ? 0.7 : 1,
              }}
            >
              {gerandoPdf ? 'Gerando PDF...' : 'PDF'}
            </button>
          )}
          {notas && (
            <button
              type="button"
              onClick={gerarExcel}
              disabled={gerandoExcel}
              style={{
                padding: '0.5rem 1rem', background: '#15803d', color: '#fff', border: 'none',
                borderRadius: 6, fontWeight: 600, fontSize: '0.85rem', cursor: gerandoExcel ? 'not-allowed' : 'pointer',
                opacity: gerandoExcel ? 0.7 : 1,
              }}
            >
              {gerandoExcel ? 'Gerando Excel...' : 'Excel'}
            </button>
          )}
        </div>
        {erro && <p style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: 8 }}>{erro}</p>}
      </div>

      {notas && (
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Notas</div>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#1a6ea8' }}>{notas.length}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Bruto</div>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#111827' }}>R$ {fmtMoeda(totalBruto)}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Líquido</div>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#15803d' }}>R$ {fmtMoeda(totalLiquido)}</div>
              </div>
            </div>
          </div>

          <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e5e7eb' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ background: '#1a6ea8' }}>
                  {['Empresa', 'Emissão', 'Cliente', 'Série', 'Número', 'Tipo', 'Status', 'CFOP'].map(h => (
                    <th key={h} style={{ padding: '0.65rem 0.8rem', textAlign: 'left', color: '#fff', fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{h}</th>
                  ))}
                  <th style={{ padding: '0.65rem 0.8rem', textAlign: 'right', color: '#fff', fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Vlr. Bruto</th>
                  <th style={{ padding: '0.65rem 0.8rem', textAlign: 'right', color: '#fff', fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Vlr. Líquido</th>
                </tr>
              </thead>
              <tbody>
                {notas.map((n, i) => (
                  <Fragment key={`${n.ntv_empcod}-${n.ntv_serie}-${n.ntv_numero}-${n.cli_cod}`}>
                    <tr style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: '1px solid #eef0f4' }}>
                      <td style={{ padding: '0.65rem 0.8rem', color: '#374151' }}>{n.ntv_empcod}</td>
                      <td style={{ padding: '0.65rem 0.8rem', color: '#374151', whiteSpace: 'nowrap' }}>{fmtData(n.ntv_datemi)}</td>
                      <td style={{ padding: '0.65rem 0.8rem', color: '#111827', fontWeight: 500 }}>{n.cliente}</td>
                      <td style={{ padding: '0.65rem 0.8rem', color: '#374151' }}>{n.ntv_serie}</td>
                      <td style={{ padding: '0.65rem 0.8rem', color: '#374151' }}>{n.ntv_numero}</td>
                      <td style={{ padding: '0.65rem 0.8rem' }}>
                        <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#eff6ff', color: '#1a6ea8' }}>{n.ntv_tipope}</span>
                      </td>
                      <td style={{ padding: '0.65rem 0.8rem' }}>
                        <span style={{
                          fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 999,
                          background: n.ntv_sitnfe === 7 ? '#fee2e2' : '#dcfce7',
                          color: n.ntv_sitnfe === 7 ? '#b91c1c' : '#15803d',
                        }}>
                          {SITUACAO_LABEL[n.ntv_sitnfe] ?? n.ntv_sitnfe}
                        </span>
                      </td>
                      <td style={{ padding: '0.65rem 0.8rem', color: '#374151' }}>{n.cfop}</td>
                      <td style={{ padding: '0.65rem 0.8rem', textAlign: 'right', color: '#374151' }}>{fmtMoeda(n.valor_bruto)}</td>
                      <td style={{ padding: '0.65rem 0.8rem', textAlign: 'right', color: '#15803d', fontWeight: 600 }}>{fmtMoeda(n.valor_liquido)}</td>
                    </tr>
                    {listarItens && n.itens?.map(it => (
                      <tr key={`${n.ntv_empcod}-${n.ntv_serie}-${n.ntv_numero}-item-${it.its_cod}-${it.pnv_des}`} style={{ background: '#fafbfc' }}>
                        <td></td>
                        <td colSpan={4} style={{ padding: '0.4rem 0.8rem', fontSize: '0.82rem', color: '#4b5563' }}>
                          ↳ <strong>{it.its_cod}</strong> — {it.pnv_des}
                        </td>
                        <td colSpan={2} style={{ padding: '0.4rem 0.8rem', fontSize: '0.82rem', color: '#6b7280' }}>
                          Qtd: {Number(it.pnv_qt_ven)}
                        </td>
                        <td colSpan={2} style={{ padding: '0.4rem 0.8rem', fontSize: '0.82rem', color: '#6b7280' }}>
                          Unit.: {fmtMoeda(it.pnv_preco)}
                        </td>
                        <td style={{ padding: '0.4rem 0.8rem', textAlign: 'right', fontSize: '0.82rem', color: '#4b5563' }}>
                          {fmtMoeda(it.pnv_vl_mer)}
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid #1a6ea8', background: '#f7faff', fontWeight: 700 }}>
                  <td colSpan={8} style={{ padding: '0.75rem 0.8rem', textAlign: 'right', color: '#1a6ea8' }}>TOTAL:</td>
                  <td style={{ padding: '0.75rem 0.8rem', textAlign: 'right', color: '#111827' }}>{fmtMoeda(totalBruto)}</td>
                  <td style={{ padding: '0.75rem 0.8rem', textAlign: 'right', color: '#15803d' }}>{fmtMoeda(totalLiquido)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
